// Where a trend line stops being one measurement.
//
// Oura freezes the 50-question set so that a moving number means the business
// moved (decision 06). A RE-AUTHOR is the one moment that stops being true: the
// same site, measured on a different set of questions, produces a different
// number for no reason a client did anything about.
//
// The workflow measured exactly this the expensive way. Miroku Hair Salon,
// 3 Aug 2026: re-authored from the July set onto the v3 Visibility Set,
// web-search visibility went 15/50 (30%) to 26/50 (52%) and #1-pick 4% to 28%.
// The projection beforehand — old per-group hit rates, re-weighted to the v3
// shape — said 23%: wrong by 29 points and wrong in DIRECTION (traps.md trap
// 12). Nothing about the salon changed.
//
// So a break is not a footnote. Joining those two points draws a claim we would
// have to defend to a client, and "-62%" is exactly the kind of number they
// react to.

/** The re-author event, as the BE records it (`results.queries_trend_break`). */
export interface TrendBreak {
    /** ISO timestamp of the re-author. */
    at: string;
    /** Plain-words reason, e.g. "question set re-authored onto visibility-set-v3". */
    reason: string;
}

/**
 * Which trend index is the FIRST point measured on the new question set.
 *
 * The break sits BETWEEN `index - 1` and `index`, so a returned `2` means the
 * segment 0→1 and the segment 2→… are each internally comparable and the gap
 * between points 1 and 2 is not.
 *
 * `null` when there is nothing to draw:
 *  - no break on this run (the usual case — the BE stamps it only when the set
 *    was re-authored AND the business had a prior scan),
 *  - no per-point dates, so the break cannot be LOCATED. Drawing it at a
 *    guessed index would put a "these are not comparable" marker between two
 *    points that are,
 *  - the break lands at or before index 0, where every point on screen was
 *    measured on the new set and there is no join to interrupt.
 */
export function trendBreakIndex(
    trend: number[] | null | undefined,
    capturedAt: (string | null)[] | null | undefined,
    brk: TrendBreak | null | undefined,
): number | null {
    if (!brk?.at || !trend || trend.length < 2) return null;
    // Index-aligned by contract (`aeo_visibility.py` builds them together). A
    // mismatched length means the alignment assumption is void, and lining them
    // up anyway would mislabel which point is which.
    if (!capturedAt || capturedAt.length !== trend.length) return null;

    const at = Date.parse(brk.at);
    if (Number.isNaN(at)) return null;

    for (let i = 0; i < capturedAt.length; i++) {
        const raw = capturedAt[i];
        if (!raw) continue;              // undated point — cannot place the break here
        const t = Date.parse(raw);
        if (Number.isNaN(t)) continue;
        // The re-authoring run's own point carries the same stamp, so `>=`
        // rather than `>`: that point IS the first one on the new set.
        if (t >= at) return i > 0 ? i : null;
    }
    // Every dated point predates the break. That happens when the re-authoring
    // run has not landed in the series yet — nothing to interrupt.
    return null;
}

/**
 * Does the headline delta span the break?
 *
 * The delta compares the newest point with the one before it. If the break sits
 * exactly there, the percentage is two rulers subtracted from each other and
 * must not be presented as a change in the business.
 */
export function deltaCrossesBreak(
    trend: number[] | null | undefined,
    breakIndex: number | null,
): boolean {
    return breakIndex !== null && !!trend && breakIndex === trend.length - 1;
}

/**
 * The question-set break between a scan and the scored scan it is compared with
 * (#4236), or null when they sit on the same ruler.
 *
 * `rows` is newest first, `newerIdx` the scan being read and `olderIdx` the scored
 * scan under it. A scan's own `visibility.trendBreak` says it was measured
 * differently from the scan before it, and that scan can be a gated run sitting
 * between the two, so every row from `newerIdx` down to (not including) `olderIdx`
 * is checked. Absent `visibility` is unknown, not "same set": no break is claimed.
 */
export function questionBreakBetween<T extends { visibility?: { trendBreak?: TrendBreak | null } | null }>(
    rows: T[],
    newerIdx: number,
    olderIdx: number,
): TrendBreak | null {
    if (newerIdx < 0 || olderIdx <= newerIdx) return null;
    for (const r of rows.slice(newerIdx, olderIdx)) {
        const brk = r.visibility?.trendBreak;
        if (brk) return brk;
    }
    return null;
}
